import moment from "moment";
import { UserMessage } from "../schemas/bt/user-message";

export const MessageOvedueLimit = 30;
// #98: max minutes a message may be re-dispatched after its FIRST send attempt. Bounds the churn
// of an already-sent-but-stuck message; anchored on first-send (not dueDate) so it never drops a
// not-yet-sent (lagging) campaign message.
export const RedispatchWindowLimit = 50;
// #128: drift floor (hours past dueDate) for a NEVER-sent campaign message. Without it a
// never-sent-but-permanently-stuck campaign message (e.g. an engine lock-fight that never
// completes) is re-dispatched forever by the gateway's schedule-service loop (observed: one
// message, 94k logs, looping every ~10s for 5 weeks). Mirrors the backend scheduler's existing
// never-sent bound (`dueDate >= 3h ago`) so FE and BE agree on eligibility.
export const CampaignNeverSentDriftFloorHours = 3;
// Minimum spacing between send attempts for a single message (server-authoritative inter-attempt
// backoff). A re-dispatch landing within this gap of the prior attempt is DENIED — this is what stops
// a proactive retry from firing inside the WhatsApp store-sync blind window (the prior send isn't yet
// visible in the engine's store) and producing a duplicate. The single source of truth shared by BOTH
// server gates (legacy `can-i-send` + `acquire-send-slot`) and the scheduler's re-dispatch lock, so the
// whole pipeline agrees on one number. MUST be <= the scheduler's re-dispatch interval, else a
// scheduler-dispatched retry gets bounced here and the effective retry interval doubles.
export const SEND_ATTEMPT_GAP_SECONDS = 180; // 3 minutes
// Server-authoritative retry budget for a single message on the acquire-send-slot path: the number of
// re-attempts after the initial send before the slot reports 'exhausted'.
// RETRY IS ACTIVE. The slot gate uses this HARD-CODED SEND_MAX_RETRIES=5 — it does NOT read the
// global `settings` retry fields at all. 5 retries × the 3-min gap fit comfortably inside the ~50-min
// re-dispatch window.
// HISTORY (describes a past state, NOT current behavior): the slot budget used to be settings-derived
// via a resolveSlotMaxRetries helper. The global settings default (isSendMessageRetryEnabled:false /
// messageMaxRetries:0, see app.ts) made that return 0 for EVERY message → acquire-send-slot exhausted
// at attempt 0 → retryCount frozen → a failed send (even a transient mqtt-failed-to-deliver) never
// advanced/retried, and the scheduler re-fired it ~300×/47min until the window gave up. After the
// 2026-06-25 deadlock RCA, resolveSlotMaxRetries was REMOVED in favor of this fixed constant, so the
// settings default (and its `0`) no longer gates the slot's retry budget.
export const SEND_MAX_RETRIES = 5;

/** FE#447: how long past its dueDate a NON-campaign message may still be sent, in ms — the per-message
 *  retry limit (`retryLimitDuration` + `retryLimitUnit`) when set and valid, else MessageOvedueLimit (30 min).
 *  The single definition behind getUpperLimit, the server's overdue gate, the reaper and the extension's
 *  local backstop, so they cannot drift. */
export const overdueWindowMs = (message: Pick<UserMessage, 'retryLimitDuration' | 'retryLimitUnit'>): number => {
  if (message.retryLimitDuration && message.retryLimitUnit) {
    const ms = moment.duration(message.retryLimitDuration, message.retryLimitUnit).asMilliseconds();
    if (Number.isFinite(ms) && ms > 0) return ms;
  }
  return MessageOvedueLimit * 60_000;
};

const getUpperLimit = (message: UserMessage) => {

  const messageDueDate = moment.utc(message.dueDate);
  let upperLimit;

  if (message.retryLimitDuration && message.retryLimitUnit) {
    upperLimit = messageDueDate.add(message.retryLimitDuration, message.retryLimitUnit);
  } else {
    upperLimit = messageDueDate.add(MessageOvedueLimit, "minutes");
  }

  return upperLimit;
}

export const isMessageIsWithinSendingLimits = (message: UserMessage) => {

  const now = moment.utc();

  // #98: campaigns only. A paced campaign staggers dueDates, so a campaign message can be sent far
  // past its own dueDate — dueDate can't bound it. Instead bound re-dispatch by the FIRST send
  // attempt: never sent yet (no firstSendAttempt) → still lagging, keep trying; already sent →
  // stop once past the window. Normal (non-campaign) messages stay dueDate-centric below — their
  // dueDate IS the intended send time.
  if (message.campaignId) {
    if (message.firstSendAttempt) {
      const sendWindowEnd = moment.utc(message.firstSendAttempt).add(RedispatchWindowLimit, "minutes");
      return now.isSameOrBefore(sendWindowEnd);
    }
    // #128: never sent yet → keep trying (paced-campaign lag is legitimate), BUT only within a
    // drift floor past dueDate. Without this a never-sent-but-stuck campaign message loops forever
    // in the gateway's schedule-service. Mirrors the backend scheduler's `dueDate >= 3h ago` bound.
    if (!message.dueDate) {
      return false;
    }
    const neverSentFloor = moment.utc(message.dueDate).add(CampaignNeverSentDriftFloorHours, "hours");
    return now.isSameOrBefore(neverSentFloor);
  }

  if (!message.dueDate) {
    return false;
  }

  const messageDueDate = moment.utc(message.dueDate);
  const lowerLimit = messageDueDate;

  const upperLimit = getUpperLimit(message);

  let isWithinLimits: boolean;
  isWithinLimits = now.isBetween(lowerLimit, upperLimit, null, '[]');

  return isWithinLimits
}


export const isMessageOverdue = (message: UserMessage) => {
  // Note: Campaign messages can also be overdue/expired if they've been in "sent" status
  // past the retry limit. We no longer skip campaign messages here.

  if (!message.dueDate) {
    return true;
  }

  const upperLimit = getUpperLimit(message);

  const now = moment.utc();

  return now.isAfter(upperLimit);
};